import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from './hooks/useAuth';
import { useProfile } from './hooks/useProfile';
import { LoadingScreen } from './components/shared/LoadingScreen';
import { AuthLayout } from './layouts/AuthLayout';
import { AppLayout } from './layouts/AppLayout';
import { SharedCategoryView } from './components/scout/SharedCategoryView';
import { PublicAthleteView } from './components/scout/PublicAthleteView';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { ArrowLeft, CheckCircle2, XCircle } from 'lucide-react';

// Lazy loaded views
const LandingPage = lazy(() =>
  import('./components/landing/LandingPage').then((m) => ({
    default: m.LandingPage,
  }))
);
const AuthScreen = lazy(() =>
  import('./components/auth/AuthScreen').then((m) => ({
    default: m.AuthScreen,
  }))
);
const OnboardingWizard = lazy(() =>
  import('./components/profile/OnboardingWizard').then((m) => ({
    default: m.OnboardingWizard,
  }))
);
const AthleteDashboard = lazy(() =>
  import('./layouts/AthleteDashboard').then((m) => ({
    default: m.AthleteDashboard,
  }))
);
const StaffDashboard = lazy(() =>
  import('./layouts/StaffDashboard').then((m) => ({
    default: m.StaffDashboard,
  }))
);
const IndependenceLanding = lazy(() =>
  import('./components/landing/IndependenceLanding').then((m) => ({
    default: m.IndependenceLanding,
  }))
);

// Read checkout result from URL params once on mount, then clear the param
function readAndClearCheckoutParam(): 'success' | 'cancel' | null {
  const params = new URLSearchParams(window.location.search);
  const result = params.get('checkout') as 'success' | 'cancel' | null;
  if (result) {
    params.delete('checkout');
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
    window.history.replaceState({}, '', newUrl);
  }
  return result;
}

// Read /scout/:athleteId from pathname once on mount, then clear it from the URL
function readAndClearScoutPath(): string | null {
  const match = window.location.pathname.match(/^\/scout\/([^/]+)$/);
  if (match) {
    window.history.replaceState({}, '', '/');
    return match[1];
  }
  return null;
}

export const AppRouter: React.FC = () => {
  const { authState, profile, updateProfile } = useAuth();
  const { isStaff } = useProfile();

  const [view, setView] = useState<'landing' | 'auth'>('landing');
  const [initialAuthMode, setInitialAuthMode] = useState<'LOGIN' | 'SIGNUP'>('LOGIN');
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'lifetime' | null>(null);
  const [checkoutResult, setCheckoutResult] = useState<'success' | 'cancel' | null>(null);
  const [scoutAthleteId, setScoutAthleteId] = useState<string | null>(null);
  const [scoutCategoryToken, setScoutCategoryToken] = useState<string | null>(null);
  const [partnerSlug, setPartnerSlug] = useState<string | null>(null);

  useEffect(() => {
    setCheckoutResult(readAndClearCheckoutParam());

    // /scout/category/:token — public, no auth required
    const categoryMatch = window.location.pathname.match(/^\/scout\/category\/([^/]+)$/);
    if (categoryMatch) {
      window.history.replaceState({}, '', '/');
      setScoutCategoryToken(categoryMatch[1]);
      return;
    }

    const athleteId = readAndClearScoutPath();
    if (athleteId) {
      setScoutAthleteId(athleteId);
      return;
    }

    // Partner / school landing pages
    const partnerMatch = window.location.pathname.match(/^\/(independence|founding|friends)$/i);
    if (partnerMatch) {
      window.history.replaceState({}, '', '/');
      setPartnerSlug(partnerMatch[1].toLowerCase());
    }
  }, []);

  if (authState === 'BOOTING') {
    return <LoadingScreen />;
  }

  // Partner / founding-offer landing slugs → the IndependenceLanding page.
  // 'independence' + 'founding' are the printed-program URLs (school-branded);
  // 'friends' is the generic-audience variant (no partnerLabel).
  const isPartnerLanding =
    partnerSlug === 'independence' || partnerSlug === 'founding' || partnerSlug === 'friends';

  return (
    <ErrorBoundary>
    <Suspense fallback={<LoadingScreen />}>
      {/* Checkout result overlay — shown on top of whatever is rendered */}
      {checkoutResult && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[700] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-sm shadow-2xl p-10 text-center space-y-6">
            {checkoutResult === 'success' ? (
              <>
                <div className="flex justify-center">
                  <div className="p-4 bg-emerald-50 rounded-full">
                    <CheckCircle2 size={40} className="text-emerald-500" />
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-black uppercase italic text-slate-900">
                    You&apos;re Pro!
                  </h2>
                  <p className="text-sm text-slate-500 font-medium mt-2">
                    Your subscription is active. Welcome to ScoutLoop Elite.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-center">
                  <div className="p-4 bg-slate-50 rounded-full">
                    <XCircle size={40} className="text-slate-400" />
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-black uppercase italic text-slate-900">
                    Checkout Cancelled
                  </h2>
                  <p className="text-sm text-slate-500 font-medium mt-2">
                    No charge was made. You can upgrade anytime from Settings.
                  </p>
                </div>
              </>
            )}
            <button
              onClick={() => setCheckoutResult(null)}
              className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black uppercase italic text-xs tracking-widest active:scale-95 transition"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {authState === 'UNAUTHENTICATED' &&
        (view === 'auth' ? (
          <AuthLayout>
            <div className="w-full max-w-md">
              <button
                onClick={() => setView('landing')}
                className="mb-8 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft size={14} /> Back to Home
              </button>
              <AuthScreen initialMode={initialAuthMode} selectedPlan={selectedPlan} />
            </div>
          </AuthLayout>
        ) : (
          <LandingPage
            onGetStarted={(plan) => {
              setSelectedPlan(plan ?? null);
              setInitialAuthMode('SIGNUP');
              setView('auth');
            }}
            onLogin={() => {
              setSelectedPlan(null);
              setInitialAuthMode('LOGIN');
              setView('auth');
            }}
          />
        ))}

      {authState === 'RECOVERING' && (
        <AuthLayout>
          <AuthScreen initialMode="UPDATE_PASSWORD" />
        </AuthLayout>
      )}

      {authState === 'AUTHENTICATED' &&
        (profile && (!profile.sport || !profile.full_name) ? (
          <OnboardingWizard
            initialName={profile.full_name || ''}
            initialBirthYear={profile.birth_year}
            onComplete={updateProfile}
          />
        ) : (
          <AppLayout>{isStaff ? <StaffDashboard /> : <AthleteDashboard />}</AppLayout>
        ))}

      {/* ScoutPass public view — no auth required, renders over any state */}
      {scoutAthleteId && (
        <PublicAthleteView athleteId={scoutAthleteId} />
      )}

      {/* Partner school landing pages — public, unauthenticated only */}
      {isPartnerLanding && authState === 'UNAUTHENTICATED' && (
        <IndependenceLanding
          partnerLabel={partnerSlug === 'friends' ? undefined : 'Independence High School'}
          onGetStarted={() => {
            setPartnerSlug(null);
            setInitialAuthMode('SIGNUP');
            setView('auth');
          }}
          onLogin={() => {
            setPartnerSlug(null);
            setInitialAuthMode('LOGIN');
            setView('auth');
          }}
        />
      )}

      {/* Shared category public view — no auth required, renders over any state */}
      {scoutCategoryToken && (
        <SharedCategoryView
          shareToken={scoutCategoryToken}
          onClose={() => setScoutCategoryToken(null)}
        />
      )}
    </Suspense>
    </ErrorBoundary>
  );
};
